import { useCallback, useState } from 'react'
import { Effect } from 'effect'
import { v7 as uuidv7 } from 'uuid'
import { datadogLogs } from '@datadog/browser-logs'
import type { Bet } from 'src/types/domain/Bet'
import type { BetDetails } from 'src/types/domain/BetDetails'
import type { MarbleGame } from 'src/types/domain/Game'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import { BetMap } from 'src/types/mappers/BetMap'
import { generateBetId } from 'src/utils/betUtils'
import { isBalanceReady } from 'src/utils/betGuards'
import { useBetActions } from 'src/store/bet'
import { usePlayerActions } from 'src/store/player'
import { useSocketStore } from 'src/store/socket'
import { useSoundEffect } from 'src/hooks/useSoundEffect'

interface PlacedChip {
  optionId: string
  marketId: string
  amount: number
  // Optional override for the bet payload shape. Side-bet rows
  // (forecast / tricast derived from the player's main-market marble
  // picks) snapshot the bet type + the actual marble IDs at placement
  // time so the submit pipeline can pass them straight through to the
  // backend's existing classic-mode forecast/tricast code paths.
  // Undefined → treated as a vanilla single-marble PickWinner bet.
  betType?: BetDetails['type']
  selectedItems?: string[]
}

/**
 * `appliedBoosters` is keyed by `optionId`. Each entry records which
 * inventory item is bound to that placement so it travels with the
 * bet payload (matches `PlaceBetPanel`'s contract — server-side
 * apply, not client-side display only).
 */
type AppliedBoosters = Record<string, { boosterId: string; percent: number }>

/**
 * `appliedFreeBets` is keyed by `optionId`. Each entry records the
 * free-bet inventory item ID claimed for that option's bet. The amount
 * has already been set on the matching `placedChips[optionId]` (the
 * panel locks the option's stake to the free-bet amount at click
 * time). Backend identifies it as a free bet from the inventory item
 * type — no additional flag needed on the DTO.
 */
type AppliedFreeBets = Record<string, { freeBetId: string; amount: number }>

interface UseChipPlacementBetSubmitProps {
  game: MarbleGame
  player: AuthenticatedPlayer
}

/**
 * Result of a submit attempt.
 *
 * - `sent`    — ids of the bets that actually hit the wire.
 * - `aborted` — true when the submit bailed on a TRANSIENT infra failure
 *   (no channel / channel not joined / no current race) with nothing sent.
 *   The caller must then keep the chips on the board and retry next round —
 *   NOT record the round as bet (which made chips look live then vanish).
 *   `false` for a clean send, the legitimate no-chips case, and the
 *   balance-not-ready pre-flight bail (a distinct, near-impossible path the
 *   caller handles quietly rather than surfacing a retry notice).
 */
export interface SubmitAllBetsResult {
  sent: string[]
  aborted: boolean
}

/**
 * A hard round's send (hard close rev 4, DESIGN §6): never on an unjoined
 * channel, and the join is waited for until the board's estimate of K − 0.5 s
 * (`joinDeadline`, `performance.now()` ms) instead of the fixed 750 ms. Past
 * it nothing is sent and the chips carry ("held for the next round").
 */
export interface SubmitAllBetsOptions {
  joinDeadline?: number
}

const perfNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

export function useChipPlacementBetSubmit({ game, player }: UseChipPlacementBetSubmitProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const betActions = useBetActions()
  const playerActions = usePlayerActions()
  const { playEffect } = useSoundEffect()

  const submitAllBets = useCallback(
    async (
      placedChips: Record<string, PlacedChip>,
      appliedBoosters: AppliedBoosters,
      appliedFreeBets: AppliedFreeBets = {},
      options?: SubmitAllBetsOptions
    ): Promise<SubmitAllBetsResult> => {
      // Safety choke point: never submit before the real balance has loaded.
      // Not an abort: distinct pre-flight guard the caller handles quietly.
      if (!isBalanceReady(player)) return { sent: [], aborted: false }
      setIsSubmitting(true)
      const submittedIds: string[] = []

      try {
        // Resolve the channel FRESH from the store on every read, not from a
        // value closed over at render/callback-creation time. On reconnect
        // the socket store swaps in a NEW player channel and nulls the old
        // one; a stale closed-over reference would leave us polling / pushing
        // on a dead channel whose pushes silently drop (the "bet:accepted
        // never arrives" path). Reading getState() each time always sees the
        // live channel.
        const getChannel = () => useSocketStore.getState().playerChannel
        if (!getChannel()) {
          datadogLogs.logger.error('chip_placement_bet_submit_no_channel', {})
          return { sent: [], aborted: true }
        }

        // Pre-flight channel state check. Phoenix pushes silently queue
        // when a channel isn't joined; queued pushes can be dropped on
        // reconnect, which is the most likely path for "bet:accepted
        // never arrives". Wait briefly for a late join (up to ~750ms)
        // before giving up — covers the case where submission triggers
        // moments before `race_starting` reconnect settles.
        // Cast: phoenix-js types omit 'joined' from the union but the
        // runtime value is 'joined' once a channel is connected.
        const isJoined = () => {
          const ch = getChannel()
          return !!ch && (ch.state as string) === 'joined'
        }
        if (!isJoined()) {
          const hardDeadline = options?.joinDeadline
          const channelJoinDeadline = Date.now() + 750
          const waiting = () =>
            hardDeadline === undefined ? Date.now() < channelJoinDeadline : perfNow() < hardDeadline
          while (!isJoined() && waiting()) {
            await new Promise<void>((r) => {
              window.setTimeout(r, 50)
            })
          }
          if (!isJoined()) {
            datadogLogs.logger.error('chip_placement_bet_submit_channel_not_joined', {
              channelState: getChannel()?.state
            })
            return { sent: [], aborted: true }
          }
        }

        // The channel to push on — resolved fresh AFTER the join wait so a
        // reconnect during the wait doesn't leave us on a stale reference.
        // The push loop below is synchronous (no awaits) so this stays live
        // for every push.
        const channel = getChannel()
        if (!channel) {
          datadogLogs.logger.error('chip_placement_bet_submit_no_channel', {})
          return { sent: [], aborted: true }
        }

        const currentRace = game.races[0]
        if (!currentRace) {
          datadogLogs.logger.error('chip_placement_bet_submit_no_race', {})
          return { sent: [], aborted: true }
        }

        const chips = Object.values(placedChips).filter((c) => c.amount > 0)
        // Legitimate empty submit (no chips on the board) — not an abort.
        if (chips.length === 0) return { sent: [], aborted: false }

        for (const chip of chips) {
          // Inventory item bound to this option, claimed at click time.
          // Free bet wins over booster when both could attach (in
          // practice they're mutually exclusive — `handleOptionClick`
          // claims a free bet first and short-circuits the booster
          // path, so a single chip carries at most one inventory item).
          // Server reads the item off `betDetails.inventoryItemIds`,
          // not a top-level field, and identifies its type (booster vs
          // free bet) from the inventory item itself — no extra flag
          // needed on the DTO.
          const freeBet = appliedFreeBets[chip.optionId]
          const booster = appliedBoosters[chip.optionId]
          const inventoryItemIds: string[] | undefined = freeBet
            ? [freeBet.freeBetId]
            : booster
              ? [booster.boosterId]
              : undefined
          // Side-bet chips snapshot the bet shape on placement; main-market
          // chips fall back to the classic single-marble PickWinner shape.
          // The backend dispatches by `type` so the existing forecast/
          // tricast probability + settlement code handles these without
          // any chip-placement-specific changes.
          const baseDetails: BetDetails = {
            type: chip.betType ?? 'PickWinner',
            selectedItems: chip.selectedItems ?? [chip.optionId],
            ...(inventoryItemIds ? { inventoryItemIds } : {})
          } as BetDetails

          const betWithoutId: Omit<Bet, 'id'> = {
            playerId: player.id,
            raceId: currentRace.uuid,
            raceConfigurationId: currentRace.raceConfigurationId,
            betAmount: chip.amount,
            currency: player.balance.currency,
            betDetails: baseDetails,
            status: 'pending',
            potentialPayout: 0,
            createdAt: new Date().toISOString(),
            // Funded by a free-bet reward — its stake is not debited, so a
            // purge/timeout of this optimistic bet must not refund it.
            isFreeBet: Boolean(freeBet)
          }

          const bet: Bet = {
            id: Effect.runSync(generateBetId(betWithoutId)),
            ...betWithoutId
          }

          const correlationId = uuidv7()
          const payload = BetMap.toPlaceBetRequestedDTO(player.sessionId, bet, correlationId)

          datadogLogs.logger.info('chip_placement_bet_submit', {
            user_event: {
              correlationId,
              marketId: chip.marketId,
              optionId: chip.optionId,
              payload
            }
          })

          // Fire-and-forget. The backend doesn't reply via Phoenix's
          // `:reply :ok` mechanism — it processes the push then
          // broadcasts `bet:accepted` or `bet:rejected` events back on
          // the player channel. Those events are routed by
          // `usePlayerListeners` → `handleBetAccepted` / `handleBetRejected`,
          // which update the bet store (`betAccepted` keeps the bet in
          // `bets.byId`; `betRejected` removes it). The panel's
          // reconciliation effect then strips chips for any bet that
          // disappeared from the store. Don't try to await an `ok`
          // here — the push will time out on every successful bet
          // because the server never replies.
          channel.push('client:bet:initiated', payload)

          betActions.betPlaced(bet)
          // The second arg is `isFreeBet` — free bets don't decrement the
          // player's wallet (the inventory item was already "spent" when
          // claimed). Without this, the optimistic balance debit briefly
          // drops the displayed balance by the free-bet amount before the
          // bet:accepted event corrects it.
          playerActions.betPlaced(bet, Boolean(freeBet))
          submittedIds.push(bet.id)
        }

        if (submittedIds.length > 0) {
          playEffect('betPlaced')
        }

        return { sent: submittedIds, aborted: false }
      } catch (error) {
        datadogLogs.logger.error('chip_placement_bet_submit_threw', {
          error: error instanceof Error ? error.message : String(error)
        })
        // A mid-loop throw may have pushed some bets already — surface those
        // as sent (not an abort): the ones on the wire are real; the caller's
        // reconcile handles any that didn't make it.
        return { sent: submittedIds, aborted: false }
      } finally {
        setIsSubmitting(false)
      }
    },
    [game, player, betActions, playerActions, playEffect]
  )

  return { submitAllBets, isSubmitting }
}
